import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  ICart,
  PATIENT_API,
  NewOrderDTO,
  NewConsultationDTO,
  UpdateConsultationDTO,
  Consultation,
  IAddress,
  IAccountDTO,
  IAccountAddress,
  IFile,
  Message,
  InboundDTO,
  ICustomerUser,
  IOrderDetailsDTO,
  PayInvoiceWithPaymentMethodDTO,
  IAppointment,
  UpdateAppointmentDTO,
  IRequiredFormsParam,
  IPendingRequirementsResponse,
  CustomerNotificationType,
  INotification,
  CouponResponse,
  ILabReq,
  LabReqPdfUrlResponse,
  JoinChatResponse,
  IChatAuth,
  IFileUploadResponseDTO,
  IOrder,
  IConsultation,
  ICartV2,
  ICheckoutStartResponseDTO,
  ICheckoutFinalizeRequestDTO,
  IPaymentMethod,
  ICoupon,
  ISubscription,
  UpdateSubscriptionDTO,
  CheckoutOrderRequirementsChecks,
  IPatientPortalLayout,
  ICheckoutSession,
  PUBLIC_API,
  FileMetadataDTO
} from '@care-portals/api-interfaces';
import {
  catchError,
  from,
  map,
  Observable,
  of,
  switchMap,
  throwError
} from 'rxjs';
import { formTypes, QuestionsCatalog } from '@care-portals/data';
import Stripe from 'stripe';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class APIService {
  API_URL = environment.api.baseUrl;

  constructor(public http: HttpClient) {}

  /**
   * App API
   */
  getAppLayout(): Observable<IPatientPortalLayout> {
    return this.http.get<IPatientPortalLayout>(
      environment.api.baseUrl + PATIENT_API.app.base + PATIENT_API.app.layout
    );
  }

  //////////////////////////
  //////// File
  //////////////////////////

  uploadFiles(files: IFile[]): Observable<IFileUploadResponseDTO[]> {
    return this.http.put<IFileUploadResponseDTO[]>(
      environment.api.baseUrl + PATIENT_API.files.base,
      files
    );
  }

  uploadFile(fileItem: IFile): Observable<IFileUploadResponseDTO> {
    const fileBlob = this.resolveFileBlob(fileItem);
    const contentType = fileItem.mimetype || fileBlob.type || 'application/octet-stream';
    const fileName = fileItem.name || `upload_${new Date().toISOString()}`;
    const metadata: FileMetadataDTO[] = [
      {
        type: fileName,
        size: fileBlob.size,
        contentType
      }
    ];

    return this.http
      .put<{ signedUrl: string; fileLocation: string }[]>(
        environment.api.baseUrl +
          PATIENT_API.files.base +
          PATIENT_API.files.uploadUrl,
        metadata
      )
      .pipe(
        catchError(error => throwError(() => error.error || error)),
        switchMap(res => {
          const signed = res?.[0];
          if (!signed?.signedUrl || !signed?.fileLocation) {
            throw new Error('Failed to generate upload URL');
          }

          return from(
            fetch(signed.signedUrl, {
              method: 'PUT',
              headers: {
                'Content-Type': contentType
              },
              body: fileBlob
            })
          ).pipe(
            map(response => {
              if (!response.ok) {
                throw new Error(`S3 upload failed with status ${response.status}`);
              }
              return {
                name: fileName,
                location: signed.fileLocation,
                key: this.extractS3KeyFromUrl(signed.fileLocation)
              };
            })
          );
        })
      );
  }

  getFile(fileName: string, folder?: string): Observable<IFile> {
    // Request a presigned S3 URL (returned on `buffer`) instead of the file's
    // base64 contents, so large files don't 502 on the Lambda 6MB response cap.
    const path = encodeURIComponent(fileName);
    let url =
      environment.api.baseUrl + PATIENT_API.files.base + `/${path}?url=true`;
    if (folder) {
      url += `&folder=${encodeURIComponent(folder)}`;
    }
    return this.http.get<IFile>(url);
  }

  getMessages(filters: any): Observable<Message[]> {
    return this.http.get<Message[]>(
      environment.api.baseUrl + PATIENT_API.messages.base,
      { params: filters }
    );
  }

  getMessage(id: string): Observable<Message> {
    return this.http.get<Message>(
      environment.api.baseUrl + PATIENT_API.messages.base + '/' + id
    );
  }

  replyMessage(body: InboundDTO): Observable<Message> {
    return this.http.post<Message>(
      environment.api.baseUrl +
        PATIENT_API.messages.base +
        PATIENT_API.messages.webInbound,
      body
    );
  }

  //////////////////////////
  //////// ORDER
  //////////////////////////
  async pullOrder(id: string): Promise<ICustomerUser> {
    const res = this.http.get<ICustomerUser>(`/orders/${id}/pull`);
    return res.toPromise();
  }

  async createConsultation(
    consultationType: string,
    orderId: number,
    customer: ICustomerUser
  ): Promise<string> {
    const res = this.http.post<string>('/consultations', {
      type: consultationType,
      orderId: orderId,
      customer
    });
    return res.toPromise();
  }

  /**
   * checkout v2
   */
  getCartPublic(org: string, cartId: string): Observable<ICartV2> {
    return this.http.get<ICartV2>(
      environment.publicApi.baseUrl +
        '/v2' +
        PUBLIC_API.carts.base +
        PUBLIC_API.carts.single.replace(':id', cartId),
      {
        headers: {
          organization: org
        }
      }
    );
  }

  getCheckoutSession(cartId: string): Observable<ICheckoutSession> {
    return this.http.get<ICheckoutSession>(
      environment.api.baseUrl +
        PATIENT_API.checkoutV2.base +
        PATIENT_API.checkoutV2.single.replace(':id', cartId)
    );
  }

  startCheckoutV2(cartId: string): Observable<ICheckoutStartResponseDTO> {
    return this.http.get<ICheckoutStartResponseDTO>(
      environment.api.baseUrl +
        PATIENT_API.checkoutV2.base +
        PATIENT_API.checkoutV2.start.replace(':id', cartId)
    );
  }

  applyCouponV2(
    cartId: string,
    code: string
  ): Observable<{ coupon: ICoupon; cart: ICartV2; error: string }> {
    return this.http.post<{ coupon: ICoupon; cart: ICartV2; error: string }>(
      environment.api.baseUrl +
        PATIENT_API.checkoutV2.base +
        PATIENT_API.checkoutV2.coupon
          .replace(':id', cartId)
          .replace(':code', code),
      {}
    );
  }

  finalizeCheckoutV2(
    cartId: string,
    finalizeCheckoutRequest: ICheckoutFinalizeRequestDTO
  ): Observable<IOrder & CheckoutOrderRequirementsChecks> {
    return this.http
      .post<IOrder & CheckoutOrderRequirementsChecks>(
        environment.api.baseUrl +
          PATIENT_API.checkoutV2.base +
          PATIENT_API.checkoutV2.finalize.replace(':id', cartId),
        finalizeCheckoutRequest
      )
      .pipe(
        catchError(error => {
          console.error('error >> ', error);
          throw error.error || error;
        })
      );
  }

  /**
   * Auth API
   */

  getCartData(cartId: string): Observable<ICart> {
    return this.http.get<ICart>(
      environment.api.baseUrl +
        PATIENT_API.checkout.base +
        PATIENT_API.checkout.items.replace(':id', cartId)
    );
  }

  linkCartToCustomer(cartId: string): Observable<ICart> {
    return this.http.get<any>(
      environment.api.baseUrl +
        PATIENT_API.checkout.base +
        PATIENT_API.checkout.start.replace(':id', cartId)
    );
  }

  checkApplyCoupon(id: string, code: string): Observable<CouponResponse> {
    return this.http.put<CouponResponse>(
      environment.api.baseUrl +
        PATIENT_API.checkout.base +
        PATIENT_API.checkout.coupon.replace(':id', id).replace(':code', code),
      {}
    );
  }

  createOrder(newOrderDto: NewOrderDTO): Observable<IOrder> {
    return this.http.post<IOrder>(
      environment.api.baseUrl + PATIENT_API.orders.base,
      newOrderDto
    );
  }

  getUserAddresses() {
    return this.http.get<IAddress[]>(
      environment.api.baseUrl +
        PATIENT_API.users.base +
        PATIENT_API.users.getAddresses
    );
  }

  /**
   * Consultation API
   */
  getConsultation(consultationId: string): Observable<IConsultation> {
    return this.http.get<IConsultation>(
      environment.api.baseUrl +
        PATIENT_API.consultations.base +
        '/' +
        consultationId
    );
  }

  saveConsultation(consultation: NewConsultationDTO): Observable<Consultation> {
    return this.http.post<Consultation>(
      environment.api.baseUrl + PATIENT_API.consultations.base,
      consultation
    );
  }

  updateConsultation(
    id: string,
    consultation: UpdateConsultationDTO
  ): Observable<Consultation> {
    return this.http.put<Consultation>(
      environment.api.baseUrl + PATIENT_API.consultations.base + '/' + id,
      consultation
    );
  }

  /**
   * Outstanding questionnaires plus the engine that answers each one.
   *
   * Uses /pending-requirements rather than the older /required-forms so
   * questionnaires migrated to Pathwai arrive tagged with a flow URL instead of
   * being rendered by the legacy form engine.
   */
  getRequiredForms(query: IRequiredFormsParam) {
    // `redirectTo` is echoed back inside the Pathwai flow URLs the API builds,
    // so a migrated questionnaire still lands the patient where patient-web
    // would have sent them.
    const params: { [key: string]: string } = {};
    if (query.orderId) {
      params.orderId = query.orderId.toString();
    } else if (query.types) {
      params.types = query.types;
    }
    if (query.redirectUrl) {
      params.redirectTo = query.redirectUrl;
    }

    return this.http.get<IPendingRequirementsResponse>(
      environment.api.baseUrl +
        PATIENT_API.consultations.base +
        PATIENT_API.consultations.pendingRequirements,
      { params }
    );
  }

  deleteDraftConsultation(type: string): Observable<any> {
    return this.http.delete(
      environment.api.baseUrl +
        PATIENT_API.consultations.base +
        PATIENT_API.consultations.draft,
      {
        params: { type }
      }
    );
  }

  getRequiredQuestions(formType: string) {
    const questions = formTypes[formType].questions;
    const realQuestions = questions.filter(
      question =>
        typeof question !== 'string' ||
        QuestionsCatalog.find(qModel => qModel.field === question)?.type !==
          'evaluated'
    );
    return of(realQuestions);
  }

  addPaymentMethod(
    paymentMethodId: string,
    zip?: string
  ): Observable<IPaymentMethod> {
    return this.http.post<IPaymentMethod>(
      environment.api.baseUrl +
        PATIENT_API.users.base +
        PATIENT_API.users.addPaymentMethods,
      { paymentMethodId, zip }
    );
  }

  removePaymentMethod(paymentMethodId: string): Observable<void> {
    return this.http.delete<void>(
      environment.api.baseUrl +
        PATIENT_API.users.base +
        PATIENT_API.users.removePaymentMethods.replace(':id', paymentMethodId)
    );
  }

  getPaymentMethods(): Observable<IPaymentMethod[]> {
    return this.http.get<IPaymentMethod[]>(
      environment.api.baseUrl +
        PATIENT_API.users.base +
        PATIENT_API.users.getPaymentMethods
    );
  }

  addDefaultAddress(address: IAddress): Observable<void> {
    if (!address) {
      return of(null);
    }

    return this.http.post<void>(
      environment.api.baseUrl +
        PATIENT_API.users.base +
        PATIENT_API.users.addDefaultAddress,
      { address }
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                                   Account                                  */
  /* -------------------------------------------------------------------------- */
  loadAccount() {
    return this.http.get<IAccountDTO>(
      environment.api.baseUrl +
        PATIENT_API.users.base +
        PATIENT_API.users.getAccount
    );
  }

  /* --------------------------------- Profile -------------------------------- */

  editAccountProfile(body) {
    return this.http.put<any>(
      environment.api.baseUrl +
        PATIENT_API.users.base +
        PATIENT_API.users.editProfile,
      body
    );
  }

  uploadIDs(files: IFile[]) {
    return this.http.put(
      environment.api.baseUrl +
        PATIENT_API.users.base +
        PATIENT_API.users.uploadIDs,
      files
    );
  }

  /* ------------------------------ Prescriptions ----------------------------- */
  getPrescriptions() {
    return this.http.get<any>(
      environment.api.baseUrl + PATIENT_API.prescriptions.base
    );
  }

  /* -------------------------------- password -------------------------------- */
  changePassword(currentPassword: string, newPassword: string) {
    return this.http.post(
      environment.api.baseUrl +
        PATIENT_API.users.base +
        PATIENT_API.users.changePassword,
      { currentPassword, newPassword }
    );
  }

  /* -------------------------------- Persona -------------------------------- */
  getPersonaLink(redirectUri: string) {
    return this.http.post(
      environment.api.baseUrl +
        PATIENT_API.users.base +
        PATIENT_API.users.personaLink,
      { redirectUri }
    );
  }

  /* -------------------------------------------------------------------------- */
  /*                                   Orders                                   */
  /* -------------------------------------------------------------------------- */
  getOrders(v: 1 | 2 = 2) {
    return this.http.get<any>(
      environment.api.baseUrl +
        (v == 2 ? PATIENT_API.ordersV2.base : PATIENT_API.orders.base)
    );
  }

  payInvoiceWithPaymentMethod(body: PayInvoiceWithPaymentMethodDTO) {
    return this.http.post<Stripe.Invoice>(
      environment.api.baseUrl +
        PATIENT_API.orders.base +
        PATIENT_API.orders.payInvoice,
      body
    );
  }

  /**
   * Get order details including products data, shipping addresses and payment method
   */
  getOrderDetails(orderId: number) {
    return this.http.get<IOrder>(
      environment.api.baseUrl +
        PATIENT_API.ordersV2.base +
        PATIENT_API.ordersV2.single.replace(':id', orderId.toString())
    );
  }

  getProduct(productId: number) {
    return this.http.get(
      environment.api.baseUrl +
        PATIENT_API.products.base +
        PATIENT_API.products.single.replace(':id', productId.toString())
    );
  }

  getSubscription(id: string) {
    return this.http.get<ISubscription>(
      environment.api.baseUrl + PATIENT_API.subscriptions.base + '/' + id
    );
  }

  updateSubscription(id: string, updates: UpdateSubscriptionDTO) {
    return this.http.put<ISubscription>(
      environment.api.baseUrl +
        PATIENT_API.subscriptions.base +
        PATIENT_API.subscriptions.single.replace(':id', id),
      updates
    );
  }

  getSubscriptions() {
    return this.http.get<any>(
      environment.api.baseUrl + PATIENT_API.subscriptions.base
    );
  }

  /* --------------------------------- Profile -------------------------------- */
  editAccountAddress(body: IAccountAddress) {
    return this.http.put<any>(
      environment.api.baseUrl +
        PATIENT_API.users.base +
        PATIENT_API.users.editAddresses,
      body
    );
  }

  /**
   * Appointment
   */

  loadAllAppointments() {
    return this.http.get<IAppointment[]>(
      environment.api.baseUrl + PATIENT_API.appointments.base
    );
  }
  loadAppointment(id: string) {
    return this.http.get<IAppointment>(
      environment.api.baseUrl + PATIENT_API.appointments.base + '/' + id
    );
  }

  updateAppointment(
    id: string,
    appointment: UpdateAppointmentDTO
  ): Observable<IAppointment> {
    return this.http.put<IAppointment>(
      environment.api.baseUrl + PATIENT_API.appointments.base + '/' + id,
      appointment
    );
  }

  getQueuePosition(id: string) {
    return this.http.get<number>(
      environment.api.baseUrl +
        PATIENT_API.appointments.base +
        PATIENT_API.appointments.getPosition.replace(':id', id.toString())
    );
  }

  /**
   * Notification
   */
  getNotifications(type: CustomerNotificationType, query: any) {
    return this.http.get<INotification[]>(
      environment.api.baseUrl +
        PATIENT_API.notifications.base +
        PATIENT_API.notifications.getNotification.replace(':type', type),
      {
        params: query
      }
    );
  }

  /**
   * Lab Reqs
   */
  getPatientLabReqs(query?: any): Observable<ILabReq[]> {
    return this.http.get<ILabReq[]>(
      environment.api.baseUrl + PATIENT_API.labReq.base,
      { params: query }
    );
  }

  getLatestPatientLabReq(): Observable<ILabReq> {
    return this.http.get<ILabReq>(
      environment.api.baseUrl + PATIENT_API.labReq.base + '/latest'
    );
  }

  getLabReqById(_id: string): Observable<ILabReq> {
    return this.http.get<ILabReq>(
      `${environment.api.baseUrl}${PATIENT_API.labReq.base}/${_id}`
    );
  }

  getLabReqPdfUrl(_id: string): Observable<LabReqPdfUrlResponse> {
    return this.http.get<LabReqPdfUrlResponse>(
      `${environment.api.baseUrl}${PATIENT_API.labReq.base}/${_id}/pdf`
    );
  }

  getLatestLabReqPdfUrl(): Observable<LabReqPdfUrlResponse> {
    return this.http.get<LabReqPdfUrlResponse>(
      `${environment.api.baseUrl}${PATIENT_API.labReq.base}/latest/pdf`
    );
  }

  //////////////////
  /// ENCOUNTERS
  //////////////////

  joinChat(appointmentId: string): Observable<JoinChatResponse> {
    return this.http.request<JoinChatResponse>(
      'put',
      environment.api.baseUrl + PATIENT_API.chat.base + PATIENT_API.chat.join,
      {
        body: {
          appointmentId
        }
      }
    );
  }

  refreshToken(token: string): Observable<IChatAuth> {
    return this.http.request<IChatAuth>(
      'put',
      environment.api.baseUrl +
        PATIENT_API.chat.base +
        PATIENT_API.chat.refreshToken,
      { body: { token } }
    );
  }

  private resolveFileBlob(fileItem: IFile): Blob {
    if (!fileItem?.buffer) {
      throw new Error('File data is empty');
    }

    if (fileItem.buffer instanceof Blob) {
      return fileItem.buffer;
    }

    if (typeof fileItem.buffer === 'string') {
      const contentType =
        fileItem.mimetype ||
        fileItem.buffer.match(/^data:([^;]+);base64,/)?.[1] ||
        'application/octet-stream';
      const base64 = fileItem.buffer.replace(/^data:(.*);base64,/, '');
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);

      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }

      return new Blob([bytes], { type: contentType });
    }

    return new Blob([fileItem.buffer], {
      type: fileItem.mimetype || 'application/octet-stream'
    });
  }

  private extractS3KeyFromUrl(fileLocation: string): string {
    try {
      return decodeURIComponent(new URL(fileLocation).pathname.replace(/^\/+/, ''));
    } catch (error) {
      return fileLocation;
    }
  }
}
